import { useState, useRef, useEffect } from "react";
import { Upload, Sparkles, Loader2, Download, RefreshCcw, Wand2 } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useNavigate } from "react-router-dom";

const PRESETS = [
    { id: "fine_dining", label: "Fine Dining", emoji: "★" },
    { id: "mexican", label: "Mexican" },
    { id: "burger_joint", label: "Burger Joint" },
    { id: "steakhouse", label: "Steakhouse" },
    { id: "seafood", label: "Seafood" },
    { id: "coffee_shop", label: "Coffee Shop" },
    { id: "bakery", label: "Bakery" },
    { id: "cocktail_bar", label: "Cocktail Bar" },
    { id: "fast_casual", label: "Fast Casual" },
    { id: "tex_mex", label: "Tex-Mex" },
    { id: "sushi", label: "Sushi" },
    { id: "pizza", label: "Pizza" },
];

const MODELS = [
    { id: "gemini-3.1-flash-image-preview", label: "Gemini Nano Banana", provider: "google" },
    { id: "gpt-image-1", label: "OpenAI GPT Image 1", provider: "openai" },
];

export default function Enhance() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const fileRef = useRef(null);
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [enhancing, setEnhancing] = useState(false);
    const [original, setOriginal] = useState(null); // { storage_path }
    const [originalLocalUrl, setOriginalLocalUrl] = useState(null);
    const [result, setResult] = useState(null);
    const [preset, setPreset] = useState("fine_dining");
    const [model, setModel] = useState(MODELS[0].id);
    const [customPrompt, setCustomPrompt] = useState("");
    const [aiPromptHelper, setAiPromptHelper] = useState(false);

    useEffect(() => {
        return () => {
            if (originalLocalUrl) URL.revokeObjectURL(originalLocalUrl);
        };
    }, [originalLocalUrl]);

    const handleFile = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Please upload an image file");
            return;
        }
        if (file.size > 12 * 1024 * 1024) {
            toast.error("Image must be under 12MB");
            return;
        }
        setUploading(true);
        setResult(null);
        try {
            const localUrl = URL.createObjectURL(file);
            setOriginalLocalUrl(localUrl);
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
            setOriginal({ storage_path: data.storage_path });
            toast.success("Image uploaded");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
        }
    };

    const onDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        const f = e.dataTransfer.files?.[0];
        handleFile(f);
    };

    const triggerEnhance = async () => {
        if (!original) {
            toast.error("Upload an image first");
            return;
        }
        if ((user?.credits_remaining ?? 0) <= 0) {
            toast.error("No credits remaining. Please upgrade your plan.");
            navigate("/pricing");
            return;
        }
        setEnhancing(true);
        setResult(null);
        try {
            let finalPrompt = customPrompt;
            if (aiPromptHelper && customPrompt) {
                try {
                    const { data: sg } = await api.post("/prompt/suggest", { description: customPrompt });
                    finalPrompt = sg.prompt;
                } catch (_e) {
                    // continue with original prompt
                }
            }
            const { data } = await api.post("/enhance", {
                image_path: original.storage_path,
                preset,
                custom_prompt: finalPrompt || null,
                model,
            });
            setResult(data);
            await refresh();
            toast.success("Magic complete!");
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Enhancement failed");
        } finally {
            setEnhancing(false);
        }
    };

    const reset = () => {
        if (originalLocalUrl) URL.revokeObjectURL(originalLocalUrl);
        setOriginal(null);
        setOriginalLocalUrl(null);
        setResult(null);
        setCustomPrompt("");
        if (fileRef.current) fileRef.current.value = "";
    };

    return (
        <AppShell subtitle="AI Photo Enhancer" title="Transform your shot." action={null}>
            {!original && (
                <div
                    className={`dropzone rounded-3xl p-10 md:p-20 text-center ${dragging ? "dragging" : ""}`}
                    onDragOver={(e) => {
                        e.preventDefault();
                        setDragging(true);
                    }}
                    onDragLeave={() => setDragging(false)}
                    onDrop={onDrop}
                    data-testid="upload-dropzone"
                >
                    <Upload className="w-10 h-10 text-orange-400 mx-auto mb-5" strokeWidth={1.5} />
                    <div className="font-heading text-2xl md:text-3xl font-semibold mb-3">Drop your phone photo here</div>
                    <div className="text-zinc-500 text-sm mb-6">JPG, PNG, WEBP · max 12MB</div>
                    <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        data-testid="upload-browse-btn"
                        className="btn-magic px-6 py-3 rounded-full text-white font-semibold inline-flex items-center gap-2 disabled:opacity-50"
                    >
                        {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                        {uploading ? "Uploading..." : "Browse files"}
                    </button>
                    <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFile(e.target.files?.[0])}
                        data-testid="upload-file-input"
                    />
                </div>
            )}

            {original && (
                <div className="grid lg:grid-cols-12 gap-8">
                    {/* Preview */}
                    <div className="lg:col-span-7">
                        {result ? (
                            <div className="rounded-3xl overflow-hidden border border-white/10 glow-orange">
                                <BeforeAfterSlider
                                    beforeSrc={originalLocalUrl}
                                    afterSrc={fileUrl(result.enhanced_path)}
                                />
                            </div>
                        ) : (
                            <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-zinc-900 aspect-[4/3]">
                                <img src={originalLocalUrl} alt="Original" className="w-full h-full object-cover" />
                                {enhancing && (
                                    <div className="absolute inset-0 grid place-items-center bg-black/55 backdrop-blur-sm">
                                        <div className="text-center">
                                            <div className="w-12 h-12 rounded-full btn-magic grid place-items-center mx-auto mb-4 pulse-glow">
                                                <Sparkles className="w-5 h-5 text-white" />
                                            </div>
                                            <div className="font-heading text-xl font-semibold mb-1">AI is working its magic...</div>
                                            <div className="text-zinc-400 text-sm">8-20 seconds, hang tight</div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        <div className="mt-4 flex flex-wrap gap-3">
                            <button onClick={reset} type="button" data-testid="enhance-reset-btn" className="px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm inline-flex items-center gap-2">
                                <RefreshCcw className="w-3.5 h-3.5" /> Upload another
                            </button>
                            {result && (
                                <a
                                    href={fileUrl(result.enhanced_path)}
                                    download={`foodglow-${result.id}.png`}
                                    data-testid="enhance-download-btn"
                                    className="px-4 py-2 rounded-full bg-white/5 hover:bg-white/10 border border-white/10 text-sm inline-flex items-center gap-2"
                                >
                                    <Download className="w-3.5 h-3.5" /> Download
                                </a>
                            )}
                        </div>
                    </div>

                    {/* Controls */}
                    <div className="lg:col-span-5 space-y-6">
                        <div className="glass-card rounded-2xl p-6">
                            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">1. Pick your style</div>
                            <div className="flex flex-wrap gap-2">
                                {PRESETS.map((p) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => setPreset(p.id)}
                                        data-testid={`enhance-preset-${p.id}`}
                                        className={`px-3.5 py-1.5 rounded-full text-sm border transition ${
                                            preset === p.id
                                                ? "bg-orange-500/15 border-orange-500/40 text-orange-300"
                                                : "bg-white/3 border-white/10 text-zinc-400 hover:bg-white/8 hover:text-white"
                                        }`}
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="glass-card rounded-2xl p-6">
                            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">2. AI model</div>
                            <div className="grid grid-cols-2 gap-2">
                                {MODELS.map((m) => (
                                    <button
                                        key={m.id}
                                        type="button"
                                        onClick={() => setModel(m.id)}
                                        data-testid={`enhance-model-${m.provider}`}
                                        className={`text-left px-3 py-2 rounded-lg border text-sm transition ${
                                            model === m.id ? "border-orange-500/40 bg-orange-500/8 text-white" : "border-white/10 bg-white/3 text-zinc-400 hover:bg-white/8"
                                        }`}
                                    >
                                        <div className="font-semibold text-sm">{m.label}</div>
                                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5">{m.provider}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="glass-card rounded-2xl p-6">
                            <div className="text-xs font-mono uppercase tracking-widest text-zinc-500 mb-3">3. Custom direction (optional)</div>
                            <textarea
                                value={customPrompt}
                                onChange={(e) => setCustomPrompt(e.target.value)}
                                rows={3}
                                placeholder='e.g. "Make this taco cinematic, add steam wisps and warm rim light"'
                                data-testid="enhance-custom-prompt"
                                className="w-full bg-black/30 border border-white/10 rounded-xl px-4 py-3 text-white placeholder:text-zinc-600 focus:outline-none focus:ring-2 focus:ring-orange-500/60 text-sm resize-none"
                            />
                            <label className="mt-3 flex items-center gap-2 text-xs text-zinc-400 cursor-pointer">
                                <input
                                    type="checkbox"
                                    checked={aiPromptHelper}
                                    onChange={(e) => setAiPromptHelper(e.target.checked)}
                                    data-testid="enhance-ai-helper-checkbox"
                                    className="accent-orange-500"
                                />
                                <Wand2 className="w-3.5 h-3.5 text-orange-400" />
                                Polish my prompt with AI Assistant
                            </label>
                        </div>

                        <button
                            type="button"
                            onClick={triggerEnhance}
                            disabled={enhancing}
                            data-testid="enhance-submit-btn"
                            className="btn-magic w-full py-4 rounded-2xl text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {enhancing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                            {enhancing ? "Enhancing..." : "Enhance image"}
                        </button>
                        <div className="text-center text-xs text-zinc-500">
                            Uses 1 credit · {user?.credits_remaining ?? 0} remaining
                        </div>
                    </div>
                </div>
            )}
        </AppShell>
    );
}
